import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import { fromMetersToKilometers } from 'noddi-util';
import type { ReactNode } from 'react';
import type { RouteRowTone } from './routeRowTone';

// The metric labels are never dimmed — an idle route recedes through its surface, not its numbers.
const STATS_CLASSES =
  'flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground whitespace-nowrap tabular-nums';

const iconVariants = cva('', {
  variants: {
    tone: {
      live: 'text-foreground',
      dim: 'text-darkPurple'
    }
  },
  defaultVariants: {
    tone: 'live'
  }
});

type Props = {
  numberOfStops: number;
  timeRange: string;
  /**
   * Actual "start - finish" shown above the planned range. `null` = not started yet: nothing shown,
   * but the wider column is kept so rows stay aligned. Omit where actual times do not apply.
   */
  actualTimeRange?: string | null;
  totalDistanceInMeters: number | undefined;
  bookingProgress?: ReactNode;
  tone?: RouteRowTone;
};

export const RouteHeaderStats = ({
  numberOfStops,
  timeRange,
  actualTimeRange,
  totalDistanceInMeters,
  bookingProgress,
  tone
}: Props) => {
  const iconClasses = iconVariants({ tone });

  return (
    <div className={STATS_CLASSES}>
      {bookingProgress && <div className='w-full sm:w-auto'>{bookingProgress}</div>}
      <div className='flex w-14 items-center gap-1.5'>
        <NoddiIcon name='MapPin' size='small' className={iconClasses} />
        <span>{numberOfStops}</span>
      </div>
      <div className={cn('flex items-center gap-1.5', actualTimeRange === undefined ? 'min-w-24' : 'min-w-40')}>
        <NoddiIcon name='Clock' size='small' className={iconClasses} />
        {actualTimeRange ? (
          // Label column keeps both times aligned.
          <div className='grid grid-cols-[auto_auto] gap-x-2'>
            <span>Actual</span>
            <span>{actualTimeRange}</span>
            <span>Plan</span>
            <span>{timeRange}</span>
          </div>
        ) : (
          <span>{timeRange}</span>
        )}
      </div>
      {totalDistanceInMeters !== undefined && (
        <div className='flex items-center gap-1.5'>
          <NoddiIcon name='Route' size='small' className={iconClasses} />
          <span>{Math.round(fromMetersToKilometers(totalDistanceInMeters))} km</span>
        </div>
      )}
    </div>
  );
};
